import gsap from 'gsap';
import classNames from 'classnames';
import type { ReactElement, RefObject } from 'react';
import { useRef } from 'react';
import type { SitePreloaderProps } from './SitePreloader.data';
import styles from './SitePreloader.module.scss';
import {
  setProgressBar,
  setupTransitionInTimeline,
  setupTransitionOutTimeline,
} from './SitePreloader.transitions';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { useMountTransition, useTransitionController } from 'util/react-transition-component';
import { useLoadGlobalAssets } from '../../../hooks/useLoadGlobalAssets';
import { useRouter } from 'next/router';
import LottieView from '../../atoms/lottie-view/LottieView';
import useIsomorphicLayoutEffect from '../../../hooks/useIsomorphicLayoutEffect';

export default function SitePreloader({
  isVisible,
  className,
  preloadComplete,
  transitionOutComplete,
  transitionOutStart,
  ref,
}: SitePreloaderProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const progressBarRef = useRef<HTMLDivElement>(null);
  const lottieHolderRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
        progressBarRef,
        lottieHolderRef,
        skipFadeOut: router.asPath === '/',
      },
      onStart(direction) {
        if (direction === 'out') {
          if (transitionOutStart) transitionOutStart();
        }
      },
      onComplete(direction) {
        if (direction === 'out') {
          if (transitionOutComplete) transitionOutComplete();
        }
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  const { isLoaded, progress } = useLoadGlobalAssets();

  useIsomorphicLayoutEffect(() => {
    setProgressBar(progress, {
      progressBarRef,
    });
  }, [progress, progressBarRef]);

  useIsomorphicLayoutEffect(() => {
    if (isLoaded) {
      // Give some time to render-page/execute JS before triggering animations.
      gsap.delayedCall(0.5, () => {
        transitionController.transitionOut();
      });

      if (preloadComplete) preloadComplete();
    }
  }, [isLoaded, preloadComplete]);

  useChangeTransitionState(transitionController, isVisible);
  useMountTransition(transitionController, true);

  return (
    <div ref={elementRef} className={classNames(styles.sitePreloader, className)}>
      <div ref={lottieHolderRef} className={classNames(styles.lottieHolder)}>
        <LottieView
          id="loader-dc"
          loop
          autoplay
          pauseWhenOutViewport={false}
          className={classNames(styles.lottieIcon, 'abs-fill')}
        />
      </div>

      <div ref={progressBarRef} className={classNames(styles.progressBar)} />
    </div>
  );
}
